/** The Trips panel (plan "Page anatomy" → Trips panel; D4, D5): the person's bookings as a
 *  schedule — where and when they go, with whom, and for a guide whether each booking is in
 *  the guide app and in its chat — split into Now · Upcoming · Past (· Undated).
 *
 *  - Header: "Trips" · the counts · the range presets (2 wk · 3 mo · 1 yr) · Open in
 *    Bookings (the Bookings tab, pinned to the person).
 *  - The schedule axis, then the groups: Now (always open), Upcoming (soonest first, the
 *    first 10 then "Show N more"), Past (most recent first, collapsed to its count; opens
 *    to 5, then pages of 25), Undated (only if any). The rows are the axis's text
 *    equivalent.
 *  - Read-only. Shown whenever the operator may list bookings (the page decides that up
 *    front, so the cards below never jump when the counts arrive): a skeleton of the axis
 *    and the rows while the groups load — a one-line one when the person's trip count says
 *    there are none — then the trips, or one line "No bookings for this person" (the band
 *    says "No trips"). A failed group shows Retry in its place and never blanks the
 *    others. */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Collapse,
  Link,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { prefersReducedMotion, useElementWidth } from '@/features/records/page/recordPage';

import { AXIS_SKELETON_PX, ScheduleAxis } from './ScheduleAxis';
import { TripRow, tripRowLayout } from './TripRow';
import {
  AXIS_RANGES,
  axisWindow,
  countsText,
  DEFAULT_AXIS_RANGE,
  dominantZone,
  emptyGroupText,
  GROUP_FIRST,
  TRIP_GROUP_LABEL,
  TRIP_GROUPS,
  TRIP_PAGE_SIZE,
  totalTrips,
  unloadedSpans,
  type AxisRangeId,
  type TripGroupId,
  type TripGroups,
} from './tripsModel';
import type { TripGroupStatus } from './useUserTrips';

export const TRIPS_ANCHOR_ID = 'user-trips';

export interface TripsPanelProps {
  groups: TripGroups;
  status: Record<TripGroupId, TripGroupStatus>;
  /** The Bookings tab (null when the viewer does not see it). */
  bookingsHref: string | null;
  now: Date;
  /** The width the rows lay out for: in edit mode the panel's width beside the docked
   *  ledger (the records core's width rule). Absent / 0 — view mode: the panel's own
   *  measured width, so a wide page gets the two-line rows. */
  layoutWidth?: number;
  /** Whether trips are expected while the groups load (the person's trip count): the
   *  skeleton takes the shape the panel will most likely end in. Default true. */
  expectTrips?: boolean;
}

/** The header row's height in every state (the range buttons'), so the loading, empty and
 *  full panels share it. */
const HEADER_MIN_PX = 30;

export function TripsPanel({ groups, status, bookingsHref, now, layoutWidth = 0, expectTrips = true }: TripsPanelProps) {
  const headingId = useId();
  const listId = useId();
  const [ref, width] = useElementWidth<HTMLDivElement>();
  const [range, setRange] = useState<AxisRangeId>(DEFAULT_AXIS_RANGE);
  const [pastOpen, setPastOpen] = useState(false);
  const [shown, setShown] = useState<Record<TripGroupId, number>>(GROUP_FIRST);
  const [pendingFocus, setPendingFocus] = useState<number | null>(null);

  const win = useMemo(() => axisWindow(range, now), [range, now]);
  const axisGroups = useMemo(
    () => (['active', 'future', 'past'] as const).map((g) => ({ group: g, rows: groups[g].rows })),
    [groups],
  );
  const zone = useMemo(() => dominantZone(axisGroups.flatMap((g) => g.rows)), [axisGroups]);
  const unloaded = useMemo(() => unloadedSpans(groups.past, groups.future, win), [groups, win]);
  const layout = tripRowLayout(layoutWidth > 0 ? layoutWidth : width);

  // A trip chosen on the axis: open its group far enough to show it, then focus its row.
  const onActivate = useCallback(
    (id: number) => {
      const group = TRIP_GROUPS.find((g) => groups[g].rows.some((r) => r.id === id));
      if (!group) return;
      const index = groups[group].rows.findIndex((r) => r.id === id);
      if (group === 'past') setPastOpen(true);
      setShown((s) => (s[group] > index ? s : { ...s, [group]: Math.ceil((index + 1) / TRIP_PAGE_SIZE) * TRIP_PAGE_SIZE }));
      setPendingFocus(id);
    },
    [groups],
  );
  useEffect(() => {
    if (pendingFocus === null) return undefined;
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(`trip-${pendingFocus}`);
      el?.scrollIntoView?.({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      el?.focus({ preventScroll: true });
      setPendingFocus(null);
    });
    return () => cancelAnimationFrame(raf);
  }, [pendingFocus]);

  const total = totalTrips(groups);
  const loading = total === null && TRIP_GROUPS.some((g) => status[g].isLoading);
  const empty = total === 0;
  // No trips — or, while loading, most likely none: a one-line panel either way.
  const compact = empty || (loading && !expectTrips);
  const loadingMore = status.past.isFetchingMore || status.future.isFetchingMore;

  let body;
  if (compact) {
    body = loading ? (
      <Skeleton variant="text" width={220} role="status" aria-label="Loading trips" sx={{ mx: 1 }} />
    ) : (
      <Typography variant="body2" color="text.secondary" sx={{ px: 1 }} data-trip-empty="all">
        No bookings for this person
      </Typography>
    );
  } else {
    body = (
      <>
        {loading ? (
          <Skeleton variant="rounded" height={AXIS_SKELETON_PX} aria-hidden data-axis-skeleton />
        ) : (
          <ScheduleAxis
            groups={axisGroups}
            window={win}
            zone={zone}
            now={now}
            earlier={unloaded.earlier}
            later={unloaded.later}
            onLoadEarlier={status.past.loadMore}
            onLoadLater={status.future.loadMore}
            loadingMore={loadingMore}
            onActivate={onActivate}
            describedBy={listId}
          />
        )}

        <Box id={listId} sx={{ display: 'grid', gap: 1.5 }} data-trip-list>
          {TRIP_GROUPS.map((g) => (
            <TripGroup
              key={g}
              group={g}
              groups={groups}
              status={status[g]}
              shown={shown[g]}
              onShowMore={() => {
                const data = groups[g];
                if (shown[g] >= data.rows.length && data.hasMore) status[g].loadMore();
                setShown((s) => ({ ...s, [g]: s[g] + TRIP_PAGE_SIZE }));
              }}
              open={g !== 'past' || pastOpen}
              onToggle={g === 'past' ? () => setPastOpen((o) => !o) : undefined}
              axisZone={zone}
              now={now}
              layout={layout}
            />
          ))}
        </Box>
      </>
    );
  }

  return (
    <Box
      component="section"
      id={TRIPS_ANCHOR_ID}
      tabIndex={-1}
      aria-labelledby={headingId}
      data-trips-panel
      sx={{
        outline: 'none',
        borderRadius: 1,
        '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
      }}
    >
      <Card variant="outlined" ref={ref}>
        <CardContent sx={{ display: 'grid', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minHeight: HEADER_MIN_PX }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flex: 1, minWidth: 200 }}>
              <Typography id={headingId} variant="subtitle2" component="h2" sx={{ color: 'text.secondary', m: 0 }}>
                Trips
              </Typography>
              {loading ? (
                <Skeleton variant="text" width={140} aria-hidden sx={{ typography: 'caption' }} />
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }} data-trip-counts>
                  {countsText(groups)}
                </Typography>
              )}
            </Box>
            {!compact && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={range}
                onChange={(_, v: AxisRangeId | null) => v && setRange(v)}
                aria-label="Schedule range"
              >
                {(Object.keys(AXIS_RANGES) as AxisRangeId[]).map((r) => (
                  <ToggleButton key={r} value={r} sx={{ px: 1.25, py: 0.125, textTransform: 'none', fontSize: 12 }}>
                    {AXIS_RANGES[r].label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
            {bookingsHref && !compact && (
              <Link component={RouterLink} to={bookingsHref} underline="hover" sx={{ typography: 'body2', fontWeight: 600 }}>
                Open in Bookings ›
              </Link>
            )}
          </Box>

          {body}
        </CardContent>
      </Card>
    </Box>
  );
}

function TripGroup({
  group,
  groups,
  status,
  shown,
  onShowMore,
  open,
  onToggle,
  axisZone,
  now,
  layout,
}: {
  group: TripGroupId;
  groups: TripGroups;
  status: TripGroupStatus;
  shown: number;
  onShowMore: () => void;
  open: boolean;
  onToggle: (() => void) | undefined;
  axisZone: string;
  now: Date;
  layout: ReturnType<typeof tripRowLayout>;
}) {
  const bodyId = useId();
  const data = groups[group];
  if (group === 'undated' && !status.error && (data.count ?? 0) === 0) return null;

  const label = TRIP_GROUP_LABEL[group];
  const count = data.count === null ? null : `${data.count.toLocaleString('en-US')}${data.capped ? '+' : ''}`;
  const title = (
    <>
      {label}
      {count !== null && (
        <Box component="span" sx={{ fontWeight: 400 }}>
          {' '}
          · {count}
        </Box>
      )}
    </>
  );

  const visible = data.rows.slice(0, shown);
  const remainingLoaded = data.rows.length - visible.length;
  const remaining = Math.max(0, (data.count ?? data.rows.length) - visible.length);
  const more = remainingLoaded > 0 ? Math.min(TRIP_PAGE_SIZE, remainingLoaded) : data.hasMore ? Math.min(TRIP_PAGE_SIZE, remaining) : 0;

  let body;
  if (status.isLoading) {
    body = (
      <Box role="status" aria-label={`Loading ${label.toLowerCase()} trips`} sx={{ display: 'grid', gap: 0.5 }}>
        {[0, 1].map((i) => (
          <Skeleton key={i} variant="rounded" height={44} />
        ))}
      </Box>
    );
  } else if (status.error && data.count === null) {
    body = (
      <Alert
        severity="error"
        sx={{ py: 0 }}
        action={
          <Button color="inherit" size="small" onClick={status.retry}>
            Retry
          </Button>
        }
      >
        Couldn’t load the {label.toLowerCase()} trips.
      </Alert>
    );
  } else if (data.rows.length === 0) {
    body = (
      <Typography variant="body2" color="text.secondary" sx={{ px: 1 }} data-trip-empty={group}>
        {emptyGroupText(group, groups, now)}
      </Typography>
    );
  } else {
    body = (
      <>
        <Box role="list" aria-label={`${label} trips`}>
          {visible.map((row) => (
            <TripRow key={row.id} row={row} axisZone={axisZone} now={now} layout={layout} />
          ))}
        </Box>
        {more > 0 && (
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 0.5 }}>
            <Button size="small" onClick={onShowMore} disabled={status.isFetchingMore}>
              {status.isFetchingMore ? 'Loading…' : `Show ${more} more`}
            </Button>
          </Box>
        )}
      </>
    );
  }

  return (
    <Box data-trip-group={group}>
      {onToggle ? (
        <ButtonBase
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={bodyId}
          sx={{
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            typography: 'caption',
            fontWeight: 600,
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
            '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
          }}
        >
          <Box component="h3" sx={{ m: 0, font: 'inherit' }}>
            {title}
          </Box>
          <ExpandMoreIcon
            aria-hidden
            sx={{ fontSize: 16, transform: open ? 'rotate(180deg)' : 'none', transition: prefersReducedMotion() ? 'none' : 'transform 150ms' }}
          />
        </ButtonBase>
      ) : (
        <Typography variant="caption" component="h3" color="text.secondary" sx={{ m: 0, px: 1, fontWeight: 600, display: 'block' }}>
          {title}
        </Typography>
      )}
      <Collapse in={open} timeout={prefersReducedMotion() ? 0 : 'auto'} id={bodyId}>
        <Box sx={{ pt: 0.5 }}>{body}</Box>
      </Collapse>
    </Box>
  );
}
